import { CollapsibleSection, Pills, Text } from '@amelio/ui-library';

import { SkillsetSkillRow } from './SkillsetSkillRow';

import type { SkillCategory, SkillItem } from '@/types/performance-profile';

/**
 * Styles layered on the library's card variant:
 * - one pastel wash for every band, so the level reads from the pill rather than the background;
 * - no white wash on header hover, so the band never flickers;
 * - a gray border that outweighs the card's white one by doubling the class selector (`!` is linted out).
 */
const CATEGORY_CARD =
    'bg-gradient-pastel-cool [&>button:hover]:bg-transparent [&&]:border-gray-200';

interface SkillsetCategorySectionProps {
    category: SkillCategory;
    /** The colour the company gave the category's level, the one its legend icon wears. */
    levelColor?: string;
    /** The level the category holds, already worded by the card's legend. */
    proficiencyLabel: string;
    isOpen: boolean;
    onToggle: () => void;
    onEditSkill: (skill: SkillItem) => void;
    onViewAssessmentHistory: (skill: SkillItem) => void;
}

/** A category of competencies, folded shut until its level is not enough of an answer. */
export const SkillsetCategorySection = ({
    category,
    levelColor,
    proficiencyLabel,
    isOpen,
    onToggle,
    onEditSkill,
    onViewAssessmentHistory,
}: SkillsetCategorySectionProps) => {
    return (
        <CollapsibleSection
            variant={'card'}
            isOpen={isOpen}
            onToggle={onToggle}
            className={CATEGORY_CARD}
            dataTestId={`skillset-category-${category.id}`}
            header={
                <div className={'flex items-center gap-3'}>
                    <Text
                        weight={'semibold'}
                        className={'text-body-lg text-navy leading-tight'}
                    >
                        {category.name}
                    </Text>

                    {category.proficiency && (
                        <Pills
                            variant={'tinted'}
                            color={levelColor}
                            size={'sm'}
                            dataTestId={`skillset-category-${category.id}-level`}
                        >
                            {proficiencyLabel}
                        </Pills>
                    )}
                </div>
            }
        >
            <div className={'divide-growth-tint flex flex-col divide-y'}>
                {category.skills.map((skill) => (
                    <SkillsetSkillRow
                        key={skill.id}
                        skill={skill}
                        onEdit={onEditSkill}
                        onViewAssessmentHistory={onViewAssessmentHistory}
                    />
                ))}
            </div>
        </CollapsibleSection>
    );
};
